import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConstants } from './constants';

export type Locale = (typeof AppConstants.locale.availableLocales)[number];

export function getUserLocale(): Locale {
  if (typeof window === 'undefined') {
    return AppConstants.locale.defaultLocale;
  }

  const locale = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${AppConstants.locale.cookieName}=`))
    ?.split('=')[1] as Locale | undefined;

  const locales = new Set(AppConstants.locale.availableLocales);
  if (locale && locales.has(locale)) {
    return locale;
  }

  return AppConstants.locale.defaultLocale;
}

export function setUserLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;

  document.cookie = `${AppConstants.locale.cookieName}=${locale}; path=/; max-age=${60 * 60 * 24 * 365}`;
  localStorage.setItem('i18nextLng', locale);
}

/** Normalize i18n/browser locale codes to the API's BCP47 format. */
export function normalizeLocale(localeCode: string): Locale {
  if (!localeCode) {
    return AppConstants.locale.defaultLocale;
  }

  const locales = new Set(AppConstants.locale.availableLocales);
  if (locales.has(localeCode as Locale)) {
    return localeCode as Locale;
  }

  const lang = localeCode.split('-')[0].toLowerCase();
  if (lang === 'ja') return 'ja-JP';
  if (lang === 'en') return 'en-US';

  return AppConstants.locale.defaultLocale;
}

export function getDisplayLanguage(
  localeCode: string,
  displayLocale = 'jp',
): string {
  if (!localeCode) {
    return AppConstants.locale.defaultLocale;
  }
  try {
    const languageNames = new Intl.DisplayNames([displayLocale], {
      type: 'language',
    });
    const regionNames = new Intl.DisplayNames([displayLocale], {
      type: 'region',
    });

    const [langCode, regionCode] = localeCode.split('-');

    const languageName = languageNames.of(langCode);
    const regionName = regionCode ? regionNames.of(regionCode) : '';

    return regionName ? `${languageName}` : languageName || localeCode;
  } catch (error) {
    return localeCode;
  }
}

export function useLocaleSyncFonts() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Update HTML lang attribute and body class based on current language
    const currentLang = i18n.language;
    document.documentElement.setAttribute('lang', currentLang);

    if (currentLang === 'ja-JP') {
      document.body.classList.add('lang-ja');
      document.body.classList.remove('lang-en');
    } else {
      document.body.classList.add('lang-en');
      document.body.classList.remove('lang-ja');
    }
  }, [i18n.language]);
}
